/**
 * Editor for the mandatory requirements an evaluation enforces.
 *
 * These are evaluation inputs — what a responsive quote must contain — and
 * they are operator-entered (optionally seeded from the project's
 * requirements register, then adjusted). The backend applies them; the
 * browser only collects them.
 */
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { MANDATORY_KINDS } from "./levelingUtils";
import { Plus, Trash2, Import } from "lucide-react";

const MandatoryRequirementsEditor = ({ value, onChange, linkedRequirements = [] }) => {
  const [draft, setDraft] = useState({ code: "", kind: "SCOPE", description: "" });

  const add = () => {
    if (!draft.code.trim() || !draft.description.trim()) return;
    onChange([...value, { ...draft, code: draft.code.trim(), description: draft.description.trim(), hard: true }]);
    setDraft({ code: "", kind: "SCOPE", description: "" });
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const importFromRegister = () => {
    const existing = new Set(value.map((r) => r.code));
    const imported = linkedRequirements
      .filter((r) => !existing.has(r.requirementKey))
      .map((r) => ({
        code: r.requirementKey,
        kind: "SCOPE",
        description: r.title,
        hard: Boolean(r.isHardGate),
      }));
    if (imported.length === 0) return;
    onChange([...value, ...imported]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">
          Mandatory requirements ({value.length}) — a quote that fails a hard one is
          disqualified; silence on one is UNKNOWN, never compliance.
        </p>
        {linkedRequirements.length > 0 && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={importFromRegister}>
            <Import className="w-3 h-3 mr-1" />
            Seed from register ({linkedRequirements.length})
          </Button>
        )}
      </div>

      {value.map((req, idx) => (
        <div key={`${req.code}-${idx}`} className="flex items-center gap-2 rounded-lg bg-muted/30 px-2.5 py-1.5">
          <Badge variant="outline" className="bg-muted/50 border-border font-normal shrink-0">
            {req.kind}
          </Badge>
          <span className="text-xs font-medium shrink-0">{req.code}</span>
          <span className="text-xs text-muted-foreground truncate flex-1">{req.description}</span>
          {req.hard && (
            <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/25 font-normal shrink-0">
              hard
            </Badge>
          )}
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={() => remove(idx)}>
            <Trash2 className="w-3 h-3 text-muted-foreground" />
          </Button>
        </div>
      ))}

      <div className="flex gap-2 items-center">
        <Input
          placeholder="Code (e.g. TESTING)"
          value={draft.code}
          onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
          className="h-8 w-36 text-xs"
        />
        <Select value={draft.kind} onValueChange={(v) => setDraft((d) => ({ ...d, kind: v }))}>
          <SelectTrigger className="h-8 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MANDATORY_KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {k}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="What a responsive quote must include"
          value={draft.description}
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          className="h-8 flex-1 text-xs"
        />
        <Button variant="outline" size="sm" className="h-8" onClick={add}>
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default MandatoryRequirementsEditor;
